import './Page.less';

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import MenuLink from '../../../MenuLink';

const messages = defineMessages({
  GoBack: { id: 'GoBack', defaultMessage: 'Back' },
});

// --- SUB-COMPONENTES MEMOIZADOS ---

const BackButton = memo(({ onBackClick, backRef }) => {
  const intl = useIntl();

  return (
    <li className="back">
      <button
        ref={backRef}
        type="button"
        onClick={onBackClick}
        className="backButton"
      >
        <span className="arrow-back">
          <i className="fa-solid fa-angle-left"></i>
        </span>
        {intl.formatMessage(messages.GoBack)}
      </button>
    </li>
  );
});

BackButton.displayName = 'BackButton';

const ParentItem = memo(({ title, url, onNavigate, lang, review_state }) => (
  <li className="parentitem">
    <MenuLink
      href={url}
      className={cx('link', { [review_state]: review_state })}
      onClick={onNavigate}
      lang={lang}
    >
      {title}
    </MenuLink>
  </li>
));

ParentItem.displayName = 'ParentItem';

// --- COMPONENTE PRINCIPAL RECURSIVO ---

const Item = memo(({ item, onSelect, onBack, onNavigate, lang }) => {
  const [active, setActive] = useState(false);
  const [childActive, setChildActive] = useState(false);

  const backRef = useRef(null);
  const openButtonRef = useRef(null);
  const subMenuContainerRef = useRef(null);

  const { title, url, items, review_state } = item;
  const hasItems = Array.isArray(items) && items.length > 0;

  // Funções de controle com useCallback para evitar re-renders desnecessários nos filhos
  const open = useCallback(() => {
    setActive(true);
    onSelect();
  }, [onSelect]);

  const close = useCallback(() => {
    setActive(false);
    onBack();
    // Devolve o foco para o botão de origem
    requestAnimationFrame(() => {
      openButtonRef.current?.focus();
    });
  }, [onBack]);

  const onChildSelect = useCallback(() => {
    setActive(false);
    setChildActive(true);
    onSelect();
  }, [onSelect]);

  const onChildBack = useCallback(() => {
    setActive(true);
    setChildActive(false);
  }, []);

  // 1. Efeito de Foco ao Abrir (Sincronizado com animação CSS de 400ms)
  useEffect(() => {
    if (active && backRef.current) {
      const timer = setTimeout(() => {
        if (backRef.current) {
          backRef.current.focus({ preventScroll: true });
          // Garante que o container esteja visível na viewport
          subMenuContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 450); // @menu-animation-delay-enter + margem de segurança
      return () => clearTimeout(timer);
    }
  }, [active]);

  // 2. Focus Trap (Loop do Tab) + Tecla ESC para Voltar
  useEffect(() => {
    if (!active || !hasItems) return;

    const container = subMenuContainerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      // ESC: Fecha apenas o nível atual (stopPropagation impede fechar tudo)
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        close();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusableElements = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [active, hasItems, close]);

  const childrenClass = cx({
    on: active,
    prev: childActive,
    off: !active && !childActive,
  });

  return (
    <li className={hasItems ? 'sub' : 'leaf'}>
      <MenuLink
        href={url}
        className={cx('link', { [review_state]: review_state })}
        onClick={onNavigate}
        lang={lang}
        tabIndex={0}
      >
        {title}
      </MenuLink>

      {hasItems && (
        <>
          <span className="br-divider vertical div" />
          <button
            ref={openButtonRef}
            type="button"
            className="br-button circle mx-1 arrow"
            onClick={open}
            aria-expanded={active}
            aria-haspopup="true"
          >
            <i className="fa-solid fa-angle-right"></i>
            <span className="sr-only">Abrir {title}</span>
          </button>
        </>
      )}

      {hasItems && (
        <div
          className={childrenClass}
          style={{ visibility: active ? 'visible' : 'hidden' }}
          aria-hidden={!active}
        >
          <ul ref={subMenuContainerRef}>
            <BackButton onBackClick={close} backRef={backRef} />

            <ParentItem
              title={title}
              url={url}
              onNavigate={onNavigate}
              lang={lang}
              review_state={review_state}
            />

            {items.map((subItem) => (
              <Item
                item={subItem}
                key={subItem['@id']}
                onSelect={onChildSelect}
                onBack={onChildBack}
                onNavigate={onNavigate}
                lang={lang}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
});

Item.displayName = 'Item';

// --- ROOT & PAGE ---

const Root = ({ items, onNavigate, lang }) => {
  const [active, setActive] = useState(true);

  const onChildSelect = useCallback(() => setActive(false), []);
  const onChildBack = useCallback(() => setActive(true), []);

  if (!items?.length) return null;

  return (
    <div className={active ? 'rooton' : 'rootprev'}>
      <ul>
        {items.map((item) => (
          <Item
            item={item}
            key={item['@id']}
            onSelect={onChildSelect}
            onBack={onChildBack}
            onNavigate={onNavigate}
            lang={lang}
          />
        ))}
      </ul>
    </div>
  );
};

const Page = ({ items, onNavigate, lang }) => {
  return (
    <div className="page-item">
      <Root items={items} onNavigate={onNavigate} lang={lang} />
    </div>
  );
};

// --- PROPTYPES ---

Item.propTypes = {
  item: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  lang: PropTypes.string,
};

Root.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onNavigate: PropTypes.func,
  lang: PropTypes.string,
};

Page.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onNavigate: PropTypes.func,
  lang: PropTypes.string,
};

export default Page;
